import { useRef, type KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/status-badge";
import { InfoHint } from "@/components/ui/info-hint";
import { X } from "lucide-react";
import type { QuantityColumn } from "@/lib/quantity-cols";
import type { PricingResult } from "@/lib/bom";

function formatAud(value: number): string {
  return value.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export interface QuantityColumnsEditorProps {
  columns: QuantityColumn[];
  pricing?: PricingResult | null;
  onChange: (next: QuantityColumn[]) => void;
  disabled?: boolean;
}

const MAX_COLUMNS = 9;

export function QuantityColumnsEditor({
  columns,
  pricing,
  onChange,
  disabled = false,
}: QuantityColumnsEditorProps) {
  // Remembers each input's committed value at focus time so Esc can revert to
  // it. Keyed by `${index}:${field}`.
  const baselines = useRef<Map<string, number>>(new Map());

  function updateField(
    index: number,
    field: keyof QuantityColumn,
    value: number,
  ) {
    const next = columns.map((col, i) =>
      i === index ? { ...col, [field]: value } : col,
    );
    onChange(next);
  }

  function handleFocus(index: number, field: keyof QuantityColumn) {
    baselines.current.set(`${index}:${field}`, columns[index]![field]);
  }

  function handleKeyDown(
    e: KeyboardEvent<HTMLInputElement>,
    index: number,
    field: keyof QuantityColumn,
  ) {
    if (e.key === "Enter") {
      // Commit: blur leaves the per-keystroke value already in place.
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      // Revert to the value captured at focus, then blur. This emits a single
      // restoring onChange carrying the original value (never the typed one).
      const baseline = baselines.current.get(`${index}:${field}`);
      if (baseline !== undefined && baseline !== columns[index]![field]) {
        updateField(index, field, baseline);
      }
      e.currentTarget.blur();
    }
  }

  function addColumn() {
    onChange([
      ...columns,
      { quantity: 0, overs: 0, markupPct: 0, adjustAmt: 0 },
    ]);
  }

  function removeColumn(index: number) {
    onChange(columns.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-muted-foreground">
        Quote the same line at several quantities — each column prices on its own.{" "}
        <span className="font-medium text-foreground">Quoted</span> is the column the quote uses.
      </p>

      <div className="flex overflow-x-auto pb-4 gap-4 items-stretch">
        
        {/* Row Labels */}
        <div className="flex flex-col gap-3 w-24 shrink-0">
          <div className="h-6 mb-2" /> {/* Space for header */}
          <div className="h-9 flex items-center text-sm font-medium text-muted-foreground">Qty</div>
          <div className="h-9 flex items-center text-sm font-medium text-muted-foreground gap-1">Overs <InfoHint label="Extra units added for spoilage, before pricing." /></div>
          <div className="h-9 flex items-center text-sm font-medium text-muted-foreground gap-1">Markup % <InfoHint label="Profit margin added to the component cost." /></div>
          <div className="h-9 flex items-center text-sm font-medium text-muted-foreground gap-1">Adjust $ <InfoHint label="Flat dollar adjustment to this column's profit (+ or −)." /></div>
          
          <div className="h-4 border-b border-slate-200 w-full" />
          
          <div className="h-6 flex items-center text-xs font-medium text-muted-foreground">Unit Price</div>
          <div className="h-6 flex items-center text-xs font-medium text-slate-700">Total (AUD)</div>
        </div>

        {/* Data Columns */}
        {columns.map((col, i) => {
          const isQuoted = i === 0;
          const resultCol = pricing?.columns.find(c => c.quantity === col.quantity);
          return (
            <div
              key={i}
              className="flex flex-col gap-3 w-28 shrink-0 bg-slate-100/50 p-2 rounded-lg border border-slate-200"
              data-testid={`quantity-col-row-${i}`}
            >
              {/* Header */}
              <div className="h-6 mb-2 flex items-center justify-between">
                {isQuoted ? (
                  <StatusBadge tone="info">Quoted</StatusBadge>
                ) : (
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Col {i + 1}</span>
                )}
                <button
                  type="button"
                  aria-label={`Remove column ${i + 1}`}
                  title={
                    columns.length <= 1
                      ? "At least one quantity column is required"
                      : undefined
                  }
                  onClick={() => removeColumn(i)}
                  disabled={disabled || columns.length <= 1}
                  className="text-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Inputs */}
              <Input
                type="number"
                aria-label={`Quantity for column ${i + 1}`}
                value={col.quantity}
                onChange={(e) => updateField(i, "quantity", Number(e.target.value) || 0)}
                onFocus={() => handleFocus(i, "quantity")}
                onKeyDown={(e) => handleKeyDown(e, i, "quantity")}
                disabled={disabled}
              />
              <Input
                type="number"
                aria-label={`Overs for column ${i + 1}`}
                value={col.overs}
                onChange={(e) => updateField(i, "overs", Number(e.target.value) || 0)}
                onFocus={() => handleFocus(i, "overs")}
                onKeyDown={(e) => handleKeyDown(e, i, "overs")}
                disabled={disabled}
              />
              <Input
                type="number"
                aria-label={`Markup % for column ${i + 1}`}
                value={col.markupPct}
                onChange={(e) => updateField(i, "markupPct", Number(e.target.value) || 0)}
                onFocus={() => handleFocus(i, "markupPct")}
                onKeyDown={(e) => handleKeyDown(e, i, "markupPct")}
                disabled={disabled}
              />
              <Input
                type="number"
                aria-label={`Adjust $ for column ${i + 1}`}
                value={col.adjustAmt}
                onChange={(e) => updateField(i, "adjustAmt", Number(e.target.value) || 0)}
                onFocus={() => handleFocus(i, "adjustAmt")}
                onKeyDown={(e) => handleKeyDown(e, i, "adjustAmt")}
                disabled={disabled}
              />

              <div className="h-4 border-b border-slate-200 w-full" />
              
              <div className="h-6 flex items-center justify-end text-sm text-muted-foreground">
                {resultCol ? formatAud(resultCol.unitPriceAud) : "-"}
              </div>
              <div className="h-6 flex items-center justify-end text-sm font-bold text-foreground">
                {resultCol ? formatAud(resultCol.totalPriceAud) : "-"}
              </div>
            </div>
          );
        })}

        {/* Add Column Button */}
        <div className="flex flex-col gap-3 w-28 shrink-0">
          <div className="h-6 mb-2" /> {/* Space for header */}
          <button
            type="button"
            aria-label="Add column"
            onClick={addColumn}
            disabled={disabled || columns.length >= MAX_COLUMNS}
            className="h-full w-full border-2 border-dashed border-slate-300 rounded-md flex flex-col items-center justify-center text-sm font-medium text-primary hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-h-[180px]"
          >
            + Add Col
          </button>
        </div>

      </div>
    </div>
  );
}
